import { action, observable, runInAction } from 'mobx';
import { AvatarColour, User, SubscriptionStatus } from '../models';
import { default as getRepository, AuthRepository } from '../repositories/Auth';
import { VideoStore } from '../stores';
import { splitNameParts } from '../utils/string';
import { ApiError } from 'app/api/fetchClient';

export class AuthStore {
  public videoStore?: VideoStore;
  public repository: AuthRepository = getRepository();

  @observable public rememberedUserFetchDone: boolean;
  @observable public currentUser?: User;
  @observable public userFetchInProgress: boolean;

  @observable public loginInProgress: boolean;
  @observable public loginError: boolean;

  @observable public signUpInProgress: boolean;
  @observable public signUpError: boolean | string;

  @observable public forgotPasswordInProgress: boolean;
  @observable public forgotPasswordError: boolean;
  @observable public forgotPasswordRequestSentToEmail?: string;

  @observable public resetPasswordInProgress: boolean;
  @observable public resetPasswordError: boolean;

  @observable public logoutInProgress: boolean;

  @observable public updateUserInProgress: boolean;
  @observable public updateUserError: boolean;

  @action
  public async login(
    username: string,
    password: string,
    rememberMe: boolean
  ): Promise<void> {
    try {
      this.loginInProgress = true;

      await this.repository.login(username, password, rememberMe);

      await this.getUserData();

      runInAction(() => (this.loginError = false));
    } catch (ex) {
      runInAction(() => (this.loginError = true));
    } finally {
      runInAction(() => (this.loginInProgress = false));
    }
  }

  @action
  public async getUserData(isInitialFetch: boolean = false): Promise<void> {
    try {
      runInAction(() => (this.userFetchInProgress = true));
      const currentUser = await this.repository.getUserData();
      runInAction(() => (this.currentUser = currentUser));

      if (this.videoStore) {
        // Reload all videos in context of new user
        await this.videoStore.reloadAll();
      }
    } catch (ex) {
      throw ex;
    } finally {
      if (isInitialFetch) {
        runInAction(() => (this.rememberedUserFetchDone = true));
      }

      runInAction(() => (this.userFetchInProgress = false));
    }
  }

  @action
  public async signUp(
    username: string,
    password: string,
    name: string,
    contactPreference: boolean
  ): Promise<void> {
    try {
      this.signUpInProgress = true;

      await this.repository.signUp(username, password, name, contactPreference);

      // TODO: FIXME - rememberMe is dangerous to set invisibly (e.g. for public computers)
      // Should add the 'keep me logged in' checkbox to register page + use that to control?
      await this.login(username, password, false);

      await this.getUserData();

      runInAction(() => (this.signUpError = false));
    } catch (ex) {
      runInAction(() => (this.signUpError = (ex as ApiError).message || true));
    } finally {
      runInAction(() => (this.signUpInProgress = false));
    }
  }

  @action
  public async forgotPassword(email: string): Promise<void> {
    try {
      this.forgotPasswordInProgress = true;

      await this.repository.forgotPassword(email);

      runInAction(() => (this.forgotPasswordError = false));
      runInAction(() => (this.forgotPasswordRequestSentToEmail = email));
    } catch (ex) {
      runInAction(() => (this.forgotPasswordError = true));
    } finally {
      runInAction(() => (this.forgotPasswordInProgress = false));
    }
  }

  @action
  public async resetPassword(
    token: string,
    email: string,
    password: string
  ): Promise<void> {
    try {
      this.resetPasswordInProgress = true;

      await this.repository.resetPassword(token, email, password);

      // TODO: FIXME - rememberMe is dangerous to set invisibly (e.g. for public computers)
      // Should add the 'keep me logged in' checkbox to reset page + use that to control?
      await this.login(email, password, false);

      await this.getUserData();

      runInAction(() => (this.resetPasswordError = false));
    } catch (ex) {
      runInAction(() => (this.resetPasswordError = true));
    } finally {
      runInAction(() => (this.resetPasswordInProgress = false));
    }
  }

  @action
  public async logout(): Promise<void> {
    const ignoreFailures = true;

    try {
      this.logoutInProgress = true;

      await this.repository.logout(ignoreFailures);

      // TODO: We currently reload the entire page as a surefire data refresh - should we
      // just reload video store data as per login?
    } catch (ex) {
      // TODO: We ignore logout failures on server - this isn't ideal, but has no UX requirements
    } finally {
      // Flag our logout as finished and remove our user
      runInAction(() => (this.currentUser = undefined));
      runInAction(() => (this.logoutInProgress = false));
    }
  }

  @action
  public async updateUser(
    name: string,
    colour: AvatarColour,
    password?: string,
    currentPassword?: string
  ): Promise<void> {
    try {
      this.updateUserInProgress = true;

      await this.repository.updateUser(name, colour, password, currentPassword);

      // Now actually apply changes to user model
      runInAction(() => {
        if (!this.currentUser) {
          return;
        }

        const { first, last } = splitNameParts(name);
        this.currentUser.colour = colour;
        this.currentUser.firstName = first;
        this.currentUser.lastName = last;
      });

      runInAction(() => (this.updateUserError = false));
    } catch (ex) {
      runInAction(() => (this.updateUserError = true));
    } finally {
      runInAction(() => (this.updateUserInProgress = false));
    }
  }

  @action
  public clearForgotPasswordError() {
    this.forgotPasswordError = false;
  }

  @action
  public clearResetPasswordError() {
    this.resetPasswordError = false;
  }

  @action
  public clearLoginError() {
    this.loginError = false;
  }

  @action
  public clearSignUpError() {
    this.signUpError = false;
  }

  @action
  public clearUpdateUserError() {
    this.updateUserError = false;
  }

  @action
  public setCurrentUserPremiumStatus(status: SubscriptionStatus) {
    const { currentUser } = this;

    if (!currentUser) {
      return;
    }

    currentUser.premium = status;
  }

  @action
  public setPremiumEndsAt(endsAt?: Date) {
    const { currentUser } = this;

    if (!currentUser) {
      return;
    }

    currentUser.premiumEndsAt = endsAt;
  }
}

export default AuthStore;



// WEBPACK FOOTER //
// ./src/app/stores/AuthStore.ts